import { IMenuItem } from '@ups/common';
import { sortByProperty, tagCleanser } from '@ups/xplat/utils';
import { IDynamicContainerMetadata, IDynamicTagDisplay } from '../models';

export interface IDynamicAppMenuInput {
  containers: Array<IDynamicContainerMetadata>;
  categories: Array<IDynamicTagDisplay>;
  menuItemDefaultPerms: Array<string | boolean>;
  isBrowser: boolean;
  staticMenuConfig?: Array<IMenuItem>;
}

/**
 * The app menu built from the dynamic container list (WI 31925: split out of DynamicRenderService.updateAppMenu).
 * Sets each container's url to its menu url, as before.
 */
export function buildDynamicAppMenu(input: IDynamicAppMenuInput): IMenuItem[] {
  let menuConfigItems: IMenuItem[] = [];
  if (input.containers && input.containers.length) {
    // create categories first
    for (let i = 0; i < input.categories.length; i++) {
      const category = input.categories[i];
      const iconClass = category.icon
        ? category.icon.substring(0, category.icon.indexOf('-'))
        : 'fa';
      menuConfigItems.push({
        path: `forms${i + 1}`,
        title: category.Name,
        tagName: category.tagName,
        isCategory: true,
        icon: category.icon || 'fa-file-contract',
        iconClass,
        exact: true,
        url: `/forms${i + 1}/${tagCleanser(category.Name)}`,
        visible:
          Array.isArray(category.visible) && category.visible?.length
            ? category.visible
            : input.menuItemDefaultPerms,
        enabled: true,
        children: [],
        disableRouting: true,
      });
    }

    menuConfigItems = menuConfigItems.sort(sortByProperty('title', true));

    const addToGenericCategory = (menuItem: IMenuItem, namePath: string) => {
      let genericCategory = menuConfigItems.find((m) => m.title === 'Forms');
      if (!genericCategory) {
        genericCategory = {
          path: `form`,
          title: 'Forms',
          isCategory: true,
          icon: 'fa-file-contract',
          iconClass: 'fa',
          exact: true,
          url: `/form/forms`,
          visible: true,
          enabled: true,
          children: [],
          disableRouting: true,
        };
        menuConfigItems.push(genericCategory);
      }
      menuItem.path = `forms/${namePath}`;
      menuItem.url = `${genericCategory.url}/${namePath}`;
      (genericCategory.children ??= []).push(menuItem);
    };
    for (const f of input.containers) {
      const dynamicContainerName = f.dynamicContainerName || '';
      const namePath = tagCleanser(dynamicContainerName);
      // this assumes the icon name is as follows {style}-{icon}
      const iconClass = f.displayIcon
        ? f.displayIcon.substr(0, f.displayIcon.indexOf('-'))
        : 'fa';
      const menuItem: IMenuItem = {
        title: dynamicContainerName,
        tooltip:
          dynamicContainerName === 'Lesson Learned'
            ? 'Submit Lessons Learned'
            : '',
        icon: f.displayIcon || 'fa-file-alt', // using a default icon in case one isn't supplied
        iconClass,
        exact: true,
        visible: true,
        enabled: true,
        children: [],
      };
      // find if a category match exist to put it in
      const containerCategoryTag = f.tags
        ? f.tags.find((t) => (t.tagName || '').indexOf('category:') > -1)
        : null;
      if (containerCategoryTag) {
        const menuCategoryMatch = menuConfigItems.find(
          (m) => m.tagName === containerCategoryTag.tagName
        );
        if (menuCategoryMatch) {
          menuItem.tagName = menuCategoryMatch.tagName;
          menuItem.path = `${tagCleanser(menuCategoryMatch.title)}/${namePath}`;
          menuItem.url = `${menuCategoryMatch.url}/${namePath}`;
          (menuCategoryMatch.children ??= []).push(menuItem);
        } else {
          addToGenericCategory(menuItem, namePath);
        }
      } else {
        addToGenericCategory(menuItem, namePath);
      }
      f.url = menuItem.url;
    }
  }
  // make sure any categories with no children are removed
  const emptyCategoryIndices: number[] = [];
  for (let i = 0; i < menuConfigItems.length; i++) {
    const children = menuConfigItems[i].children ?? [];
    if (children.length === 0) {
      emptyCategoryIndices.push(i);
    } else {
      // alphabetize children
      menuConfigItems[i].children = children.sort(
        sortByProperty('title', true)
      );
    }
  }
  for (const emptyIndex of [...emptyCategoryIndices].reverse()) {
    menuConfigItems.splice(emptyIndex, 1);
  }
  // divide those which are dynamic from those hard coded for example purposes
  if (input.isBrowser) {
    menuConfigItems.push({
      path: `admin`,
      title: '----',
      icon: '',
      exact: true,
      url: `/admin`,
      visible: true,
      enabled: true,
      children: [],
      disableRouting: true,
    });
  }
  return menuConfigItems.concat(input.staticMenuConfig || []);
}
